/**
 * Document language — decoupled from UI locale.
 * Resume, cover letter, and section headers follow the job description language.
 */

import { parseCanvasSectionKeyFromText } from '../canvas/canvasSectionUtils';
import type { CanvasSection, JobContext } from '../canvas/CanvasProvider';

export type DocumentLocale = 'en' | 'es' | 'pt' | 'ru';
export type DocumentLocaleSource = 'auto' | 'manual';

export const DOCUMENT_LOCALES: DocumentLocale[] = ['en', 'es', 'pt', 'ru'];

const SECTION_TITLES: Record<DocumentLocale, Record<string, string>> = {
  en: {
    experience: 'Work Experience',
    summary: 'Professional Summary',
    skills: 'Technical Skills',
    projects: 'Projects & Achievements',
    education: 'Education',
    certifications: 'Certifications',
    references: 'References',
    awards: 'Awards',
    research: 'Research',
    languages: 'Languages',
    personal: 'Personal Information',
  },
  es: {
    experience: 'Experiencia Laboral',
    summary: 'Resumen Profesional',
    skills: 'Habilidades Técnicas',
    projects: 'Proyectos y Logros',
    education: 'Educación',
    certifications: 'Certificaciones',
    references: 'Referencias',
    awards: 'Premios',
    research: 'Investigación',
    languages: 'Idiomas',
    personal: 'Información Personal',
  },
  pt: {
    experience: 'Experiência Profissional',
    summary: 'Resumo Profissional',
    skills: 'Habilidades Técnicas',
    projects: 'Projetos e Conquistas',
    education: 'Formação',
    certifications: 'Certificações',
    references: 'Referências',
    awards: 'Prêmios',
    research: 'Pesquisa',
    languages: 'Idiomas',
    personal: 'Informações Pessoais',
  },
  ru: {
    experience: 'Опыт работы',
    summary: 'Профессиональное резюме',
    skills: 'Технические навыки',
    projects: 'Проекты и достижения',
    education: 'Образование',
    certifications: 'Сертификаты',
    references: 'Рекомендации',
    awards: 'Награды',
    research: 'Исследования',
    languages: 'Языки',
    personal: 'Личная информация',
  },
};

export const OUTPUT_LANGUAGE_NAMES: Record<DocumentLocale, string> = {
  en: 'English',
  es: 'Spanish',
  pt: 'Brazilian Portuguese',
  ru: 'Russian',
};

export function normalizeDocumentLocale(value?: string | null): DocumentLocale {
  const code = (value ?? 'en').split('-')[0]?.toLowerCase();
  if (code === 'es' || code === 'pt' || code === 'ru') return code;
  return 'en';
}

/** Heuristic JD language detection (en / es / pt / ru). */
export function detectLanguageFromText(text: string): DocumentLocale {
  const sample = text.slice(0, 4000).trim();
  if (!sample) return 'en';

  const cyrillic = (sample.match(/[а-яёА-ЯЁ]/g) ?? []).length;
  const latin = (sample.match(/[a-zA-Záéíóúñãõçàèê]/g) ?? []).length;
  if (cyrillic > 12 && cyrillic > latin * 0.15) return 'ru';

  const ptScore = (
    (sample.match(/\b(você|não|ção|ções|currículo|experiência|habilidades|empresa|vaga)\b/gi) ?? []).length
    + (sample.match(/[ãõç]/g) ?? []).length
  );
  const esScore = (
    (sample.match(/\b(experiencia|habilidades|descripción|empresa|años|puesto|laboral)\b/gi) ?? []).length
    + (sample.match(/[áéíóúñ¿¡]/g) ?? []).length
  );

  if (ptScore >= 3 && ptScore > esScore) return 'pt';
  if (esScore >= 3) return 'es';

  return 'en';
}

export function detectLanguageFromJob(job: Pick<JobContext, 'description' | 'title' | 'requirements' | 'skills'>): DocumentLocale {
  const blob = [
    job.description ?? '',
    job.title ?? '',
    ...(job.requirements ?? []),
    ...(job.skills ?? []),
  ].join('\n');
  return detectLanguageFromText(blob);
}

export function sectionDisplayTitle(sectionKey: string, locale: DocumentLocale): string {
  const key = sectionKey.toLowerCase().trim();
  return SECTION_TITLES[locale][key] ?? SECTION_TITLES.en[key] ?? sectionKey;
}

export function relabelCanvasSections(
  sections: CanvasSection[],
  locale: DocumentLocale,
): CanvasSection[] {
  return sections.map((row) => {
    const key = parseCanvasSectionKeyFromText(row.title);
    if (!key) return row;
    return { ...row, title: sectionDisplayTitle(key, locale) };
  });
}

export function getOutputLanguageName(locale: DocumentLocale): string {
  return OUTPUT_LANGUAGE_NAMES[locale] ?? 'English';
}

export function referencesFooter(locale: DocumentLocale): string {
  const map: Record<DocumentLocale, string> = {
    en: 'Available upon request.',
    es: 'Disponibles bajo solicitud.',
    pt: 'Disponíveis mediante solicitação.',
    ru: 'Предоставляются по запросу.',
  };
  return map[locale];
}
